/**
 * Ticket and change-request files, read through copilot-api instead of public links (ACOP-123).
 *
 * Every file a customer sees on a ticket or CR — an Attachments entry or an image pasted into an
 * agent's reply — now comes from `GET /apis/{case|changerequest}/file/download?data={recordId,docId}`.
 * copilot-api takes the email from the session, asks Apex whether this viewer may see this file on
 * this record (BR-3, BR-4), and streams the bytes back. Copilot no longer creates or uses public
 * links (BR-5).
 *
 * Free of React on purpose: this decides what an answer means and what a Blob may be, and the
 * callers (FeedBody, TicketTab, ChangeRequestTab) decide what to render. That is what makes the
 * ordering and allowlist rules below directly unit-testable, the same reasoning as kbArticleFetch.ts.
 */
import { toast } from "react-toastify";
import environment from "../config/environment";
import { handleSessionExpired } from "../config/common";
import { safeJson } from "./kbArticleFetch";

export type RecordKind = "case" | "changeRequest";

export type PreviewKind = "image" | "pdf" | "download";

/** copilot-api's serve cap (D6). A file above it gets "too large" instead of a request. */
export const MAX_SERVE_BYTES = 104857600;

/** At most this many inline-image requests in flight per tab (D7). */
export const MAX_INLINE_IMAGE_REQUESTS = 4;

/** The attribute Apex puts on a pasted image the viewer may see (contract 3). */
export const DOC_ID_ATTR = "data-copilot-doc-id";

/** Cache value for an image that could not be shown. */
export const INLINE_IMAGE_FAILED = "failed";

/** Reader-facing messages for opening an attachment (AC-22). */
export const FILE_MESSAGES = {
  unavailable: "This file isn't available",
  tooLarge: "This file is too large to open here, ask Support",
} as const;

/**
 * The server's Content-Type allowlist (contract 4), mirrored here. Nothing outside it is ever
 * previewed or given an active type in this origin: svg, html, xml and ico download instead.
 */
const CONTENT_TYPES: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  bmp: "image/bmp",
  pdf: "application/pdf",
};

const ALLOWED_CONTENT_TYPES = Object.keys(CONTENT_TYPES).map((ext) => CONTENT_TYPES[ext]);

const OCTET_STREAM = "application/octet-stream";

const SF_ID_RE = /^[a-zA-Z0-9]{15}(?:[a-zA-Z0-9]{3})?$/;
const DOC_ID_RE = /^069[a-zA-Z0-9]{12}(?:[a-zA-Z0-9]{3})?$/;

function isRecordId(value: unknown): value is string {
  return typeof value === "string" && SF_ID_RE.test(value);
}

function isDocId(value: unknown): value is string {
  return typeof value === "string" && DOC_ID_RE.test(value);
}

function extensionOf(name: string | null | undefined, ext: string | null | undefined): string {
  let e = typeof ext === "string" ? ext.trim() : "";
  if (!e && typeof name === "string" && name.includes(".")) {
    e = name.trim().split(".").pop() || "";
  }
  return e.replace(/^\./, "").toLowerCase();
}

/** image / pdf / download, from exactly the server allowlist (AC-21). */
export function previewKindFor(name: string | null | undefined, ext: string | null | undefined): PreviewKind {
  const extension = extensionOf(name, ext);
  // Own keys only, so an extension like "constructor" or "__proto__" can't match something
  // inherited from Object.prototype (the API uses a Map for the same reason).
  const type = Object.prototype.hasOwnProperty.call(CONTENT_TYPES, extension) ? CONTENT_TYPES[extension] : undefined;
  if (!type) return "download";
  return type === "application/pdf" ? "pdf" : "image";
}

/** The download name: the Title, plus its extension when the Title lacks it. */
export function fileNameWithExtension(name: string | null | undefined, ext: string | null | undefined): string {
  const base = (typeof name === "string" && name.trim()) || "download";
  const e = typeof ext === "string" ? ext.trim().replace(/^\./, "") : "";
  if (!e || base.toLowerCase().endsWith("." + e.toLowerCase())) return base;
  return base + "." + e;
}

/**
 * The Blob type, from the UI's own allowlist. Only a response Content-Type that is one of the
 * allowlisted types survives; anything else (svg, html, missing, mislabelled) becomes
 * application/octet-stream. The file's extension is never used to invent a type (SEC-6).
 */
function allowlistedType(contentType: string): string {
  const base = contentType.split(";")[0].trim().toLowerCase();
  return ALLOWED_CONTENT_TYPES.indexOf(base) !== -1 ? base : OCTET_STREAM;
}

export function recordFileUrl(kind: RecordKind, recordId: string, docId: string): string {
  const path = kind === "changeRequest" ? "changerequest/file/download" : "case/file/download";
  return environment.BASE_URL + path + "?data=" + encodeURIComponent(JSON.stringify({ recordId, docId }));
}

export type RecordFileResult =
  | { ok: true; blob: Blob }
  /** session: the shared session-expired flow already ran. The caller adds nothing. */
  | { ok: false; reason: "session" | "tooLarge" | "failed"; status?: number };

/**
 * Fetch one file through the proxy. Never toasts; the caller decides what a failure looks like.
 *
 * Raw fetch (the CitationTwo pattern), so the axios interceptor's own 401/403 toasts never fire for
 * a background image. ORDER MATTERS (UI-4):
 *
 *   1. JSON envelope 401 or HTTP 401 → the shared session-expired flow. The global middleware
 *      answers a missing session with HTTP 200 and the real status INSIDE a JSON body, so a check
 *      on res.ok alone would read it as a file.
 *   2. Any other JSON → a failure. A JSON body is never file bytes. The handler's 413 is JSON too,
 *      so "too large" is read here as well as in step 3.
 *   3. HTTP 413 → too large.
 *   4. Any other non-OK → a failure.
 *   5. Otherwise the bytes, re-typed from the allowlist.
 */
export async function fetchRecordFile(kind: RecordKind, recordId: string, docId: string): Promise<RecordFileResult> {
  if (!isRecordId(recordId) || !isDocId(docId)) return { ok: false, reason: "failed" };

  let res: Response;
  try {
    res = await fetch(recordFileUrl(kind, recordId, docId), {
      method: "GET",
      credentials: "include",
      headers: { Authorization: "Bearer " + environment.BEARER_TOKEN },
    });
  } catch {
    return { ok: false, reason: "failed" };
  }

  try {
    const contentType = (res.headers.get("content-type") || "").toLowerCase();
    const isJson = contentType.includes("application/json");
    const envelopeStatus = isJson ? safeJson(await res.text())?.status : null;

    if (res.status === 401 || envelopeStatus === 401) {
      handleSessionExpired();
      return { ok: false, reason: "session", status: 401 };
    }
    if (isJson) {
      const status = typeof envelopeStatus === "number" ? envelopeStatus : res.status;
      return { ok: false, reason: res.status === 413 || status === 413 ? "tooLarge" : "failed", status };
    }
    if (res.status === 413) return { ok: false, reason: "tooLarge", status: 413 };
    if (!res.ok) return { ok: false, reason: "failed", status: res.status };

    const body = await res.blob();
    return { ok: true, blob: new Blob([body], { type: allowlistedType(contentType) }) };
  } catch {
    return { ok: false, reason: "failed" };
  }
}

/** How long a download's object URL is kept after the click. Revoking at once can cancel it. */
const DOWNLOAD_URL_LIFETIME_MS = 30000;

/** Save a Blob under `name` through a temporary <a download>. */
export function downloadBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name || "download";
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  try {
    a.click();
  } finally {
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_URL_LIFETIME_MS);
  }
}

/**
 * One tab's inline images, shared by every FeedBody in it.
 *
 * `entries` maps a docId to its object URL, INLINE_IMAGE_FAILED, or the in-flight load. The cache
 * outlives the ticket modal body on purpose: opening a preview unmounts that body and closing it
 * remounts it, and the remounted FeedBodys re-apply what is cached instead of fetching again.
 *
 * `generation` changes on every revoke (record switch, modal close, unmount). A load that settles
 * under an older generation is dropped, so one record's image can never land on another's.
 */
export type InlineImageCache = {
  entries: Map<string, string | Promise<void>>;
  generation: number;
  active: number;
  queue: Array<() => void>;
  /** The object URL of the attachment currently (or last) in the preview. */
  previewUrl: string | null;
};

export function createInlineImageCache(): InlineImageCache {
  return { entries: new Map(), generation: 0, active: 0, queue: [], previewUrl: null };
}

/** Revoke every object URL the cache holds and drop any load still queued or in flight. */
export function revokeInlineImageCache(cache: InlineImageCache): void {
  cache.generation += 1;
  cache.entries.forEach((entry) => {
    if (typeof entry === "string" && entry !== INLINE_IMAGE_FAILED) URL.revokeObjectURL(entry);
  });
  cache.entries.clear();
  if (cache.previewUrl) {
    URL.revokeObjectURL(cache.previewUrl);
    cache.previewUrl = null;
  }
  // Queued starts see the new generation and resolve without running.
  const dropped = cache.queue;
  cache.queue = [];
  dropped.forEach((start) => start());
}

/** The text a hidden or failed image leaves behind, matching Apex's own placeholder. */
export function imagePlaceholderText(alt: string | null | undefined): string {
  return typeof alt === "string" && alt.trim() ? "[Image: " + alt + "]" : "[Image]";
}

/** Always a text node, so an alt carrying markup can only ever read as text. */
function replaceWithPlaceholder(img: HTMLImageElement): void {
  const text = document.createTextNode(imagePlaceholderText(img.getAttribute("alt")));
  if (img.parentNode) img.parentNode.replaceChild(text, img);
}

const boundImages = new WeakSet<HTMLImageElement>();

function showLoadedImage(img: HTMLImageElement, docId: string, url: string, cache: InlineImageCache): void {
  if (img.getAttribute("src") !== url) img.setAttribute("src", url);
  img.setAttribute("data-testid", "comment-inline-image");
  if (boundImages.has(img)) return;
  boundImages.add(img);
  // Bytes that arrive but will not decode as an image get the placeholder too, and no toast.
  img.addEventListener("error", () => {
    if (cache.entries.get(docId) === url) {
      cache.entries.set(docId, INLINE_IMAGE_FAILED);
      URL.revokeObjectURL(url);
    }
    replaceWithPlaceholder(img);
  });
}

/**
 * Apply what the cache already knows to every marked image under `root`, and return the docIds it
 * knows nothing about yet (each once). Idempotent: a loaded image whose src is already its cached
 * URL is left alone, so this can run after every render and remount.
 */
export function applyInlineImages(root: ParentNode | null, cache: InlineImageCache): string[] {
  const missing: string[] = [];
  if (!root) return missing;
  root.querySelectorAll<HTMLImageElement>("img[" + DOC_ID_ATTR + "]").forEach((img) => {
    const docId = img.getAttribute(DOC_ID_ATTR);
    const entry = isDocId(docId) ? cache.entries.get(docId) : INLINE_IMAGE_FAILED;
    if (entry === INLINE_IMAGE_FAILED) {
      replaceWithPlaceholder(img);
    } else if (typeof entry === "string") {
      showLoadedImage(img, docId as string, entry, cache);
    } else if (entry === undefined && missing.indexOf(docId as string) === -1) {
      missing.push(docId as string);
    }
  });
  return missing;
}

function runLimited(cache: InlineImageCache, generation: number, task: () => Promise<void>): Promise<void> {
  return new Promise<void>((resolve) => {
    const start = () => {
      if (cache.generation !== generation) {
        resolve();
        return;
      }
      cache.active += 1;
      task()
        .catch(() => undefined)
        .then(() => {
          cache.active -= 1;
          const next = cache.queue.shift();
          if (next) next();
          resolve();
        });
    };
    if (cache.active < MAX_INLINE_IMAGE_REQUESTS) start();
    else cache.queue.push(start);
  });
}

export type InlineImageLoadOptions = {
  kind: RecordKind;
  recordId: string;
  /** Called once per image that settles for the current record, so the tab can re-apply. */
  onSettled?: () => void;
};

/**
 * Load images the cache knows nothing about yet: one request per docId, at most
 * MAX_INLINE_IMAGE_REQUESTS at a time. Never toasts — a failed image is a placeholder (AC-20); an
 * expired session goes through the shared flow in fetchRecordFile, which is its one message.
 */
export function loadInlineImages(cache: InlineImageCache, docIds: string[], options: InlineImageLoadOptions): Promise<void> {
  const generation = cache.generation;
  const loads: Promise<void>[] = [];
  docIds.forEach((docId) => {
    if (cache.entries.has(docId)) return;
    const load = runLimited(cache, generation, async () => {
      const result = await fetchRecordFile(options.kind, options.recordId, docId);
      if (cache.generation !== generation) return;
      let entry = INLINE_IMAGE_FAILED;
      if (result.ok && result.blob.type.indexOf("image/") === 0) {
        try {
          entry = URL.createObjectURL(result.blob);
        } catch {
          entry = INLINE_IMAGE_FAILED;
        }
      }
      cache.entries.set(docId, entry);
      if (options.onSettled) options.onSettled();
    });
    cache.entries.set(docId, load);
    loads.push(load);
  });
  return Promise.all(loads).then(() => undefined);
}

/** An Attachments entry: the ACOP-123 shape, or the pre-ACOP-123 one during the release window. */
export type RecordAttachment = {
  docId?: string;
  name?: string;
  Name?: string;
  fileExtension?: string;
  contentSize?: number | string;
  filePublicUrl?: string;
};

export type AttachmentHandlers = {
  /** Show the preview modal for an object URL this module created. */
  onPreview: (url: string, name: string, kind: PreviewKind) => void;
  onBusy?: (busy: boolean) => void;
  /** When given, the preview's object URL is revoked with this cache (or by the next preview). */
  cache?: InlineImageCache;
};

function blobMatches(blob: Blob, kind: PreviewKind): boolean {
  if (kind === "image") return blob.type.indexOf("image/") === 0;
  if (kind === "pdf") return blob.type === "application/pdf";
  return false;
}

/**
 * Open an Attachments entry that has a docId (AC-13, AC-14, AC-21, AC-22): images and PDFs preview,
 * everything else downloads under its name. One toast on any failure.
 */
export async function openRecordAttachment(
  kind: RecordKind,
  recordId: string,
  file: RecordAttachment,
  handlers: AttachmentHandlers
): Promise<void> {
  const title = file.name || file.Name || "";
  if (Number(file.contentSize) > MAX_SERVE_BYTES) {
    toast.error(FILE_MESSAGES.tooLarge);
    return;
  }

  if (handlers.onBusy) handlers.onBusy(true);
  let result: RecordFileResult;
  try {
    result = await fetchRecordFile(kind, recordId, file.docId as string);
  } finally {
    if (handlers.onBusy) handlers.onBusy(false);
  }

  if (result.ok === false) {
    if (result.reason === "tooLarge") toast.error(FILE_MESSAGES.tooLarge);
    else if (result.reason === "failed") toast.error(FILE_MESSAGES.unavailable);
    return;
  }

  const name = fileNameWithExtension(title, file.fileExtension);
  const previewKind = previewKindFor(title, file.fileExtension);
  if (previewKind === "download" || !blobMatches(result.blob, previewKind)) {
    downloadBlob(result.blob, name);
    return;
  }
  const url = URL.createObjectURL(result.blob);
  if (handlers.cache) {
    if (handlers.cache.previewUrl) URL.revokeObjectURL(handlers.cache.previewUrl);
    handlers.cache.previewUrl = url;
  }
  handlers.onPreview(url, name, previewKind);
}
